import gsap from "gsap";

import Experience from "../Experience";
import Airports from "./Airports";
import Drawers from "./Drawers";
import Hotspots from "./Hotspots";
import Labels from "./Labels"
import Overlays from "./Overlays";
import TowerInterior from "./TowerInterior";
import Vehicles from "./Vehicles";
import ZoomWidget from "./ZoomWidget";

function hideElement (event, target) {
  event.preventDefault();

  gsap.to(target, { autoAlpha: 0, ease: "none" });
}

function tweenControls (target, { x, y, z }) {
  return gsap.to(
    target,
    {
      x,
      y,
      z,
      ease: "power4.inOut",
      duration: 2,
    }
  )
}

function showHotspots (target) {
  return gsap.to(
    target,
    {
      autoAlpha: 1,
      yPercent: 0,
      stagger: 0.05,
      ease: "back.out"
    },
  );
}

function hideHotspots (target) {
  return gsap.to(
    target,
    {
      autoAlpha: 0,
      yPercent: 25,
      stagger: 0.05,
      ease: "power4.in"
    }
  )
}

export default class World {
  constructor () {
    this.experience = new Experience();
    this.airports = new Airports();
    this.vehicles = new Vehicles();
    this.labels = new Labels();
    this.hotspots = new Hotspots();
    this.drawers = new Drawers();
    this.overlays = new Overlays();
    this.towerInterior = new TowerInterior();
    this.zoomWidget = new ZoomWidget(document.querySelector(".zoom-widget"));
    this.controls = this.experience.controls;
    this.showGrowthModal = true;
    this.vision = document.querySelector(".view-toggles input:checked").value;

    this.setIntroScreen();
  }

  enterTower (hotspot) {
    const [x, y, z] = hotspot.dataset.position.split(",");
    const hotspotsToHide = [
      this.hotspots.hybrid.instances,
      this.hotspots.tower.instances,
      this.hotspots.cameraCoverage.instances
    ];

    this.lastCameraState = {
      x: gsap.getProperty(this.controls.target, "x"),
      y: gsap.getProperty(this.controls.target, "y"),
      z: gsap.getProperty(this.controls.target, "z"),
    }

    if (this.isGrowth) hotspotsToHide.push(this.hotspots.growth.instances);

    this.drawers.close();

    const tl = gsap
    .timeline()
    .add(hideHotspots(hotspotsToHide))
    .add(
      tweenControls(
        this.controls.target, {
          x: -this.controls.bounds.x * 0.5,
          y: -this.controls.bounds.y * 0.5,
          z: -this.controls.bounds.z,
        }
      ),
      "crossfade"
    )
    .to(this.towerInterior.container, { autoAlpha: 1, ease: "none" }, "crossfade")
    .to(this.isGrowth ? ".hybrid-airport" : ".growth-airport", { autoAlpha: 0, ease: "none"}, "crossfade")
    .to(".view-toggles-container", { autoAlpha: 0, ease: "none"}, "crossfade")
    .add(showHotspots(this.hotspots.towerInterior.instances), ">-=0.25")
    .to(".exit-tower", { autoAlpha: 1, ease: "none" }, ">-=0.125")
  }

  exitTower () {
    const hotspotsToShow = [
      this.hotspots.hybrid.instances,
      this.hotspots.tower.instances
    ]
    const { x, y, z } = this.lastCameraState;

    if (this.isGrowth) hotspotsToShow.push(this.hotspots.growth.instances, this.hotspots.cameraCoverage.instances);

    this.drawers.close();

    gsap
    .timeline()
    .add(hideHotspots(this.hotspots.towerInterior.instances))
    .add(tweenControls(this.controls.target, { x, y, z }), "crossfade")
    .add(showHotspots(hotspotsToShow), "crossfade")
    .to([".exit-tower", this.towerInterior.container], { autoAlpha: 0, ease: "none" }, "crossfade")
    .to(this.isGrowth ? ".hybrid-airport" : ".growth-airport", { autoAlpha: 1, ease: "none"}, "crossfade")
    .to(".view-toggles-container", { autoAlpha: 1, ease: "none"}, "crossfade")
  }

  setIntroScreen () {
    const mainLogo = document.querySelector(".intro a");
    const explore = document.querySelector(".intro button");
    const exitTower = document.querySelector(".exit-tower");
    const toggles = document.querySelectorAll(".view-toggles input");
    const growthAirport = document.querySelector(".growth-airport");
    const hybirdAirport = document.querySelector(".hybrid-airport");
    const cookieConsent = document.querySelector(".cookie-notice button");
    const operationalModalClose = document.querySelector("#operational-modal .close");
    const growthModalClose = document.querySelector("#growth-modal .close");

    gsap.set(
      [
        "#operational-modal",
        "#growth-modal",
        ".view-toggles",
        ".hotspot",
        "#camera-lines--hybrid",
        "#camera-lines--growth",
        growthAirport,
        hybirdAirport,
        exitTower,
        this.towerInterior.container,
        this.vehicles.growth.airplane.element,
        this.vehicles.growth.car.element,
        this.vehicles.growth.fuelEngine.element,
        this.vehicles.growth.tow.element,
      ],
      {
        autoAlpha: 0
      }
    );

    gsap.set(
      [
        this.hotspots.hybrid.instances,
        this.hotspots.tower.instances,
        this.hotspots.towerInterior.instances,
        this.hotspots.growth.instances,
        this.hotspots.cameraCoverage.instances,
      ],
      {
        autoAlpha: 0,
        yPercent: 25
      }
    )

    const linesTween = {
      ease: "none",
      duration: 10,
      strokeDashoffset: 650,
      repeat: -1,
    };
    const linesTween1 = {
      ease: "none",
      duration: 5,
      strokeDashoffset: -400,
      repeat: -1,
    };
    this.cameraLines = {
      hybrid: gsap.timeline({paused: true}).to("#camera-lines--hybrid .glow", linesTween, 0).to("#camera-lines--hybrid .glow-1", linesTween1, 0),
      growth: gsap.timeline({paused: true}).to("#camera-lines--growth .glow", linesTween, 0).to("#camera-lines--growth .glow-1", linesTween1, 0),
    }

    mainLogo.addEventListener("click", () => this.reloadPage());
    explore.addEventListener("click", () => this.startExplore());
    exitTower.addEventListener("click", () => this.exitTower());
    growthAirport.addEventListener("click", () => this.showGrowthAirport());
    hybirdAirport.addEventListener("click", () => this.showHybridAirport());
    cookieConsent.addEventListener("click", (event) => hideElement(event, ".cookie-notice"));
    operationalModalClose.addEventListener("click", (event) => hideElement(event, "#operational-modal"));
    growthModalClose.addEventListener("click", (event) => hideElement(event, "#growth-modal"));

    for (const toggle of toggles) {
      toggle.addEventListener("input", (e) => this.toggleHotspots(e));
    }
  }

  showGrowthAirport () {
    this.isGrowth = true;

    this.drawers.close();

    const tl = gsap
    .timeline()
    .to(".growth-airport", { duration: 0.2, autoAlpha: 0, ease: "none" }, "start")
    .to(".hybrid-airport", { duration: 0.2, autoAlpha: 1, ease: "none" }, "start")
    .add(
      tweenControls(
        this.controls.target,
        {
          x: -this.controls.bounds.x * 0.5,
          y: -this.controls.bounds.y * 0.5,
          z: -this.controls.bounds.z,
        }
      ),
      "start"
    )
    .set(".hotspot[data-section='section-4']", { left: "85%", top: "39%" })
    .call(() => this.cameraLines.growth.play())
    .to([this.airports.hybrid, "#camera-lines--hybrid"], { autoAlpha: 0, ease: "none" })
    .call(() => this.cameraLines.hybrid.pause())
    .to(
      [
        ".view-toggles",
        "#camera-lines--growth",
        this.vehicles.growth.airplane.element,
        this.vehicles.growth.car.element,
        this.vehicles.growth.fuelEngine.element,
        this.vehicles.growth.tow.element,
      ],
      {
        autoAlpha: 1,
        ease: "none"
      },
      "-=0.25"
    )
    .add(
      showHotspots([
        this.hotspots.cameraCoverage.instances,
        this.hotspots.growth.instances
      ])
    )
    .call(() => {
      this.vehicles.animations.growth.airplane.play();
      this.vehicles.animations.growth.tow.play();
    });

    if (this.showGrowthModal) tl.to("#growth-modal", { autoAlpha: 1, ease: "none" });

    this.showGrowthModal = false;
  }

  showHybridAirport () {
    this.isGrowth = false;

    this.drawers.close();

    gsap
    .timeline({
      onComplete: () => {
        this.vision = "all";

        const input = document.querySelector("#view-all");
        if (input) input.checked = true;
      }
    })
    .to(".growth-airport", { duration: 0.2, autoAlpha: 1, ease: "none" }, "start")
    .to(".hybrid-airport", { duration: 0.2, autoAlpha: 0, ease: "none" }, "start")
    .to(".view-toggles", { autoAlpha: 0, ease: "none"}, "start")
    .add(
      hideHotspots([
        this.hotspots.cameraCoverage.instances,
        this.hotspots.growth.instances
      ]),
      "start"
    )
    .add(
      tweenControls(
        this.controls.target,
        {
          x: -this.controls.bounds.x * 0.25,
          y: -this.controls.bounds.y * 0.75,
          z: 0,
        }
      ),
      "start"
    )
    .set(".hotspot[data-section='section-4']", { left: "66%", top: "59%" })
    .add(
      showHotspots(
        [
          this.hotspots.hybrid.instances,
          this.hotspots.tower.instances
        ]
      )
    )
    .call(() => this.cameraLines.hybrid.play())
    .to([this.airports.hybrid, "#camera-lines--hybrid"], { autoAlpha: 1, ease: "none" }, "crossfade")
    .to([
      "#camera-lines--growth",
      this.vehicles.growth.airplane.element,
      this.vehicles.growth.car.element,
      this.vehicles.growth.fuelEngine.element,
      this.vehicles.growth.tow.element,
      ],
      {
        autoAlpha: 0,
        ease: "none"
      },
      "crossfade"
    )
    .call(() => {
      this.cameraLines.growth.pause();
      this.vehicles.animations.growth.airplane.pause();
      this.vehicles.animations.growth.tow.pause();
    })
  }

  startExplore () {
    const overlap = "-=0.25";

    gsap
    .timeline({
      onComplete: () => this.controls.setListeners()
    })
    .to(
      ".intro > :not(h1)",
      {
        autoAlpha: 0,
        ease: "none"
      },
      overlap
    )
    .add(
      tweenControls(
        this.controls.target,
        {
          x: -this.controls.bounds.x * 0.25,
          y: -this.controls.bounds.y * 0.75,
          z: 0,
        }
      ),
      overlap
    )
    .to(this.airports.standard, { autoAlpha: 0, ease: "none" })
    .to(
      [
        ".growth-airport",
        "#operational-modal"
      ],
      {
        autoAlpha: 1,
        ease: "none",
        stagger: 0.1
      }
    )
    .add(
      showHotspots([
        this.hotspots.hybrid.instances,
        this.hotspots.tower.instances
      ]),
      overlap
    )
    .call(() => this.cameraLines.hybrid.play())
    .to("#camera-lines--hybrid", { autoAlpha: 1, ease: "none" })
  }

  toggleHotspots (e) {
    this.vision = e.target.value;

    if (this.vision === "all") {

      gsap.to(".hotspot:not([data-type=tower-interior])", { autoAlpha: 1, yPercent: 0, ease: "none" });

    } else {

      gsap.to(".hotspot", { autoAlpha: 0, yPercent: 25, ease: "none" });

      gsap.to(
        [
          `.hotspot[data-vision=${this.vision}]`,
          ".hotspot[data-vision=all]"
        ],
        {
          autoAlpha: 1,
          yPercent: 0,
          ease: "none"
        }
      );
    }
  }
}